import { Tooltip } from "@mantine/core";
import { IconMapPinOff } from "@tabler/icons-react";
import { useMemo } from "react";
import { useClient } from "#/hook/useClient";
import type { PrvPattern, PrvPatternsView } from "#/types/prv";
import { formatDate } from "#/utils/dateTimeUtils";
import {
  averageStaySeconds,
  formatDuration,
  intensityPer30Days,
  itemsPerPeriod,
  rhythmPeriodDays,
} from "./prvUtils";
import { usePrvTranslation } from "./usePrvTranslation";

export interface PrvSelection {
  subClientId: number | null;
  productId: number | null;
}

interface Props {
  view: PrvPatternsView;
  selection: PrvSelection;
  onSelect: (selection: PrvSelection) => void;
}

/** One sequential hue, light → dark; step 0 is the "never observed" surface. */
const STEPS = [
  { bg: "bg-gray-50", text: "text-gray-400" },
  { bg: "bg-blue-100", text: "text-gray-900" },
  { bg: "bg-blue-200", text: "text-gray-900" },
  { bg: "bg-blue-300", text: "text-gray-900" },
  { bg: "bg-blue-600", text: "text-white" },
  { bg: "bg-blue-800", text: "text-white" },
];

/** Sub-client column; names wrap to two lines when they do not fit. */
const LABEL_COLUMN = "20rem";
/** Narrowest a category column may get: a value plus a two-line header stay readable. */
const MIN_COLUMN = "6rem";

/**
 * Boundaries between the five shades, at the 20/40/60/80 % points of the
 * observed values (quantiles). Every shade is then used and one very busy
 * pair cannot flatten all the others into the palest blue.
 */
function quantileThresholds(values: number[]): number[] {
  const sorted = [...values].filter((v) => v > 0).sort((a, b) => a - b);
  if (sorted.length === 0) return [];
  return [0.2, 0.4, 0.6, 0.8].map((q) => {
    const position = (sorted.length - 1) * q;
    const lower = Math.floor(position);
    const upper = Math.ceil(position);
    return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
  });
}

/** 0 = never observed; 1–5 = shade, one step per threshold the value exceeds. */
function stepFor(value: number, thresholds: number[]): number {
  if (value <= 0) return 0;
  return 1 + thresholds.filter((threshold) => value > threshold).length;
}

/**
 * Sub-clients × categories grid. Cell colour = how often the category moves
 * through the sub-client (stays per 30 days). Click a cell to drill into it;
 * click a row or column header to select just that axis.
 */
const PrvHeatmap = ({ view, selection, onSelect }: Props) => {
  const t = usePrvTranslation();
  const { client } = useClient();

  const { columns, hiddenCategories, cells, thresholds } = useMemo(() => {
    const byKey = new Map<string, PrvPattern>();
    const activeProducts = new Set<number>();
    const values: number[] = [];
    for (const pattern of view.patterns) {
      byKey.set(`${pattern.subClientId}:${pattern.productId}`, pattern);
      if (pattern.stays > 0 || pattern.present > 0) activeProducts.add(pattern.productId);
      values.push(intensityPer30Days(pattern));
    }
    const shown = view.categories.filter((category) => activeProducts.has(category.id));
    return {
      columns: shown,
      hiddenCategories: view.categories.length - shown.length,
      cells: byKey,
      thresholds: quantileThresholds(values),
    };
  }, [view]);

  const isColumnSelected = (productId: number) =>
    selection.productId === productId && selection.subClientId === null;
  const isRowSelected = (subClientId: number) =>
    selection.subClientId === subClientId && selection.productId === null;

  if (columns.length === 0) {
    return (
      <p className="text-sm text-gray-500">{t("clients.prv.results.heatmap.nothingObserved")}</p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto">
        {/*
          The grid takes the full width: the sub-client column is fixed, the
          category columns share the rest equally, never narrower than a
          readable minimum (two-line header + value). Below that minimum the
          table overflows and the wrapper scrolls horizontally with the
          sub-client column kept sticky.
        */}
        <table
          className="w-full table-fixed border-separate border-spacing-[3px] text-sm"
          style={{ minWidth: `calc(${LABEL_COLUMN} + ${columns.length} * ${MIN_COLUMN})` }}
        >
          <colgroup>
            <col style={{ width: LABEL_COLUMN }} />
            {columns.map((category) => (
              <col key={category.id} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-white" />
              {columns.map((category) => (
                <th key={category.id} className="px-1 pb-1 align-bottom">
                  <button
                    type="button"
                    onClick={() => onSelect({ subClientId: null, productId: category.id })}
                    className={`flex w-full flex-col items-center gap-1 rounded px-1 py-0.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 ${
                      isColumnSelected(category.id)
                        ? "bg-gray-900 text-white hover:bg-gray-900"
                        : ""
                    }`}
                    title={category.name}
                  >
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: category.color ?? "#9ca3af" }}
                    />
                    <span className="line-clamp-2 text-center break-words whitespace-normal">
                      {category.name}
                    </span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.subClients.map((sub) => (
              <tr key={sub.id}>
                <th className="sticky left-0 z-10 bg-white pr-2 text-left align-middle">
                  <button
                    type="button"
                    onClick={() => onSelect({ subClientId: sub.id, productId: null })}
                    className={`flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-xs font-semibold text-gray-700 hover:bg-gray-100 ${
                      isRowSelected(sub.id) ? "bg-gray-900 text-white hover:bg-gray-900" : ""
                    }`}
                    title={sub.name}
                  >
                    <span className="line-clamp-2 break-words whitespace-normal">{sub.name}</span>
                    {!sub.hasCoordinates && (
                      <Tooltip label={t("clients.prv.results.heatmap.noCoordinates")} withArrow>
                        <IconMapPinOff size={14} className="shrink-0 text-orange-600" />
                      </Tooltip>
                    )}
                  </button>
                </th>
                {columns.map((category) => {
                  const pattern = cells.get(`${sub.id}:${category.id}`);
                  const value = pattern ? intensityPer30Days(pattern) : 0;
                  const step = stepFor(value, thresholds);
                  const style = STEPS[step];
                  const selected =
                    selection.subClientId === sub.id && selection.productId === category.id;
                  const periodDays = pattern ? rhythmPeriodDays(pattern) : 30;
                  const perPeriod = pattern ? itemsPerPeriod(pattern, periodDays) : null;
                  return (
                    <td key={category.id} className="h-10 p-0">
                      <Tooltip
                        withArrow
                        multiline
                        maw={260}
                        label={
                          pattern ? (
                            <div className="flex flex-col gap-0.5 text-xs">
                              <span className="font-semibold">
                                {sub.name} · {category.name}
                              </span>
                              <span>
                                {t("clients.prv.results.table.arrivals")}: {pattern.stays}
                              </span>
                              <span>
                                {t("clients.prv.results.table.departures")}:{" "}
                                {pattern.completedStays}
                              </span>
                              <span>
                                {t("clients.prv.results.table.present")}: {pattern.present}
                              </span>
                              {perPeriod !== null && (
                                <span>
                                  {t("clients.prv.results.table.rate")}:{" "}
                                  {t("clients.prv.results.arrivalsPerDays", {
                                    arrivals: perPeriod.toFixed(1),
                                    days: periodDays,
                                  })}
                                </span>
                              )}
                              <span>
                                {t("clients.prv.results.table.avgTime")}:{" "}
                                {formatDuration(averageStaySeconds(pattern), t)}
                              </span>
                              <span>
                                {t("clients.prv.results.table.last")}:{" "}
                                {formatDate(pattern.lastMovementAt, client)}
                              </span>
                            </div>
                          ) : (
                            t("clients.prv.results.heatmap.never")
                          )
                        }
                      >
                        <button
                          type="button"
                          onClick={() => onSelect({ subClientId: sub.id, productId: category.id })}
                          className={`flex h-full min-h-10 w-full items-center justify-center rounded text-xs font-semibold tabular-nums transition ${style.bg} ${style.text} ${
                            selected
                              ? "ring-2 ring-gray-900 ring-offset-1"
                              : "hover:ring-2 hover:ring-gray-400"
                          }`}
                        >
                          {pattern && value > 0 ? value.toFixed(1) : "–"}
                        </button>
                      </Tooltip>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
        {/* Swatches with the value each shade starts at, so the colours can be read as numbers */}
        <div className="flex items-center gap-1">
          <span className="mr-1">{t("clients.prv.results.heatmap.legendLow")}</span>
          {STEPS.slice(1).map((step, index) => (
            <span key={index} className="flex items-center gap-1">
              {index > 0 && (
                <span className="tabular-nums">{thresholds[index - 1]?.toFixed(1)}</span>
              )}
              <span className={`h-3 w-5 rounded-sm ${step.bg}`} />
            </span>
          ))}
          <span className="ml-1">{t("clients.prv.results.heatmap.legendHigh")}</span>
        </div>
        <span>{t("clients.prv.results.heatmap.unit")}</span>
        {hiddenCategories > 0 && (
          <span>
            {t("clients.prv.results.heatmap.hiddenCategories", { count: hiddenCategories })}
          </span>
        )}
      </div>
    </div>
  );
};

export default PrvHeatmap;
